import { useEffect, useState } from 'react'
import type { ParticipantView } from '../../event/state'

const REEL = ['Idea', 'Build', 'Ship', 'Brick', 'Spark', 'Impact', 'Team', 'Demo']
const SPIN_MS = 1800
const TICK_MS = 90

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

/**
 * The team reveal. It presents an assignment the server already saved; the
 * spin is decoration over a fixed result, so nothing here chooses anything.
 * With reduced motion the result shows straight away.
 */
function Reels({ names }: { names: string[] }) {
  const [spinning, setSpinning] = useState(() => !prefersReducedMotion())
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!spinning) return
    const interval = setInterval(() => setTick((t) => t + 1), TICK_MS)
    const stop = setTimeout(() => setSpinning(false), SPIN_MS)
    return () => {
      clearInterval(interval)
      clearTimeout(stop)
    }
  }, [spinning])

  return (
    <div className="grid gap-3 sm:grid-cols-2" aria-hidden="true">
      {names.map((name, i) => (
        <div
          key={name}
          className="flex min-h-20 items-center justify-center rounded-lg border-2 border-[var(--color-royal)] bg-[var(--page-canvas)] px-4 text-center text-2xl font-black"
        >
          {spinning ? REEL[(tick + i * 3) % REEL.length] : name}
        </div>
      ))}
    </div>
  )
}

export default function TeamReveal({ view }: { view: ParticipantView }) {
  if (view.name === null) {
    return (
      <p className="rounded-lg border border-[var(--page-rule)] p-5">
        You are not on the team list yet. Let an organizer know and they will add you.
      </p>
    )
  }

  if (!view.revealed || !view.team) {
    return (
      <div className="rounded-lg border border-[var(--page-rule)] bg-[var(--page-canvas-sunken)] p-5">
        <p className="text-lg font-bold">Teams are revealed at kickoff.</p>
        <p className="mt-1 text-[var(--page-ink-muted)]">This page updates on its own when they are.</p>
      </div>
    )
  }

  const names = view.team.members.map((m) => m.name)
  const announcement = `Your team: ${names.join(' and ')}, Team ${view.team.name}.${view.mission ? ` Mission: ${view.mission.mission}, ${view.mission.clientDomain}, ${view.mission.technology}.` : ''}`

  return (
    <div className="flex flex-col gap-4">
      <p className="sr-only" role="status">
        {announcement}
      </p>
      <p className="inline-flex items-center gap-2 text-xl font-black">
        <span aria-hidden="true" className="inline-block size-6 rounded-full border border-[var(--page-rule)]" style={{ background: view.team.color }} />
        Team {view.team.name}
      </p>
      <Reels names={names} />
      {view.mission && (
        <dl className="grid gap-3 rounded-lg border border-[var(--page-rule)] p-5 sm:grid-cols-3">
          <div>
            <dt className="text-sm font-bold text-[var(--page-accent-text)]">Mission</dt>
            <dd className="mt-1 font-semibold">{view.mission.mission}</dd>
          </div>
          <div>
            <dt className="text-sm font-bold text-[var(--page-accent-text)]">Domain</dt>
            <dd className="mt-1 font-semibold">{view.mission.clientDomain}</dd>
          </div>
          <div>
            <dt className="text-sm font-bold text-[var(--page-accent-text)]">Technology</dt>
            <dd className="mt-1 font-semibold">{view.mission.technology}</dd>
          </div>
        </dl>
      )}
    </div>
  )
}
